import type { Pt, Shape } from './utils/cgal';

const PAD = 1;

const interp = (a: Pt, b: Pt, alpha: number): Pt => {
  return [a[0] + (b[0] - a[0]) * alpha, a[1] + (b[1] - a[1]) * alpha];
};

const dedupeRing = (pts: Pt[], eps = 1e-6): Pt[] => {
  const out: Pt[] = [];
  for (const p of pts) {
    if (out.length === 0) {
      out.push(p);
      continue;
    }
    const last = out[out.length - 1];
    if (Math.hypot(p[0] - last[0], p[1] - last[1]) > eps) out.push(p);
  }
  if (out.length > 1) {
    const a = out[0];
    const b = out[out.length - 1];
    if (Math.hypot(a[0] - b[0], a[1] - b[1]) < eps) out.pop();
  }
  return out;
};

const ptsAttr = (pts: Pt[]) => pts.map(p => `${p[0].toFixed(3)},${p[1].toFixed(3)}`).join(' ');

export const AnimationCanvas = ({ shape, t }: { shape: Shape; t: number }) => {
  const { bounds, original, weights, traces, ridges, wavefronts } = shape;
  const viewBox = `${bounds.x - PAD} ${bounds.y - PAD} ${bounds.w + 2 * PAD} ${bounds.h + 2 * PAD}`;
  const K = bounds.y + bounds.h + PAD;
  // Stroke widths and dot radii were tuned for ~10-unit polygons. Scale by
  // the bounding-box's longest side so big polygons don't look hair-thin.
  const u = Math.max(bounds.w, bounds.h) / 10;

  return (
    <svg
      style={{
        width: '100%',
        height: 460,
        border: '1px solid #d0d0d0',
        background: '#fafafa',
        display: 'block',
      }}
      viewBox={viewBox}
      preserveAspectRatio="xMidYMid meet"
    >
      <g transform={`translate(0, ${K}) scale(1, -1)`}>
        {/* original polygon outline */}
        <polygon
          points={ptsAttr(original)}
          fill="none"
          stroke="#bbb"
          strokeWidth={0.035 * u}
          strokeDasharray={`${0.16 * u} ${0.1 * u}`}
        />

        {/* per-edge weights — overlay non-default weights with thicker colored stroke */}
        {weights?.map((w, i) => {
          if (w === 1) return null;
          const a = original[i];
          const b = original[(i + 1) % original.length];
          const color = w === 0 ? '#222' : w > 1 ? '#2e7d32' : '#888';
          const width = (w === 0 ? 0.13 : w > 1 ? 0.1 : 0.08) * u;
          return (
            <line
              key={`w-${i}`}
              x1={a[0]}
              y1={a[1]}
              x2={b[0]}
              y2={b[1]}
              stroke={color}
              strokeWidth={width}
              strokeLinecap="butt"
            />
          );
        })}

        {/* wavefront polygons (one per active phase × subpolygon) */}
        {wavefronts.map((wf, i) => {
          if (!(t >= wf.tStart - 1e-9 && t <= wf.tEnd + 1e-9)) return null;
          const span = wf.tEnd - wf.tStart;
          const alpha = span > 0 ? Math.max(0, Math.min(1, (t - wf.tStart) / span)) : 1;
          const pts = wf.vertices.map(v => interp(v.at_tStart, v.at_tEnd, alpha));
          const ded = dedupeRing(pts);
          if (ded.length >= 3) {
            return (
              <polygon
                key={`wf-${i}`}
                points={ptsAttr(ded)}
                fill="rgba(68,136,204,0.13)"
                stroke="#4488cc"
                strokeWidth={0.05 * u}
                strokeLinejoin="round"
              />
            );
          }
          if (ded.length === 2) {
            return (
              <line
                key={`wf-${i}`}
                x1={ded[0][0]}
                y1={ded[0][1]}
                x2={ded[1][0]}
                y2={ded[1][1]}
                stroke="#4488cc"
                strokeWidth={0.05 * u}
              />
            );
          }
          return null;
        })}

        {/* skeleton vertex traces (lines drawn over their lifespan) */}
        {traces.map((tr, i) => {
          if (t < tr.tStart - 1e-9) return null;
          const tEff = Math.min(t, tr.tEnd);
          const span = tr.tEnd - tr.tStart;
          const alpha = span > 0 ? (tEff - tr.tStart) / span : 1;
          const cur = interp(tr.from, tr.to, alpha);
          return (
            <line
              key={`tr-${i}`}
              x1={tr.from[0]}
              y1={tr.from[1]}
              x2={cur[0]}
              y2={cur[1]}
              stroke="#cc4444"
              strokeWidth={0.06 * u}
              strokeLinecap="round"
            />
          );
        })}

        {/* ridges (born at events, drawn fully thereafter) */}
        {ridges.map((r, i) => {
          if (t < r.tBorn - 1e-9) return null;
          return (
            <line
              key={`r-${i}`}
              x1={r.from[0]}
              y1={r.from[1]}
              x2={r.to[0]}
              y2={r.to[1]}
              stroke="#cc4444"
              strokeWidth={0.06 * u}
              strokeLinecap="round"
            />
          );
        })}

        {/* original vertex dots */}
        {original.map((v, i) => (
          <circle key={`o-${i}`} cx={v[0]} cy={v[1]} r={0.09 * u} fill="#777" />
        ))}

        {/* live wavefront vertex dots */}
        {wavefronts.map((wf, wi) => {
          if (!(t >= wf.tStart - 1e-9 && t <= wf.tEnd + 1e-9)) return null;
          const span = wf.tEnd - wf.tStart;
          const alpha = span > 0 ? Math.max(0, Math.min(1, (t - wf.tStart) / span)) : 1;
          return wf.vertices.map((v, vi) => {
            const cur = interp(v.at_tStart, v.at_tEnd, alpha);
            return (
              <circle
                key={`v-${wi}-${vi}`}
                cx={cur[0]}
                cy={cur[1]}
                r={0.085 * u}
                fill={v.reflex ? '#cc7700' : '#4488cc'}
                stroke="#fff"
                strokeWidth={0.02 * u}
              />
            );
          });
        })}
      </g>
    </svg>
  );
};
